import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../../../redux/hooks';
import Box from '@mui/material/Box';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import 'moment/locale/fr';
import 'moment/locale/en-ca';
import useMediaQuery from '@mui/material/useMediaQuery';

// Utils 

import SettingsRow from './SettingsRow';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { setIsNavigationDropdownOpened } from '../../../../redux/features/general/navigation';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import BubbleChartOutlinedIcon from '@mui/icons-material/BubbleChartOutlined';
import moment from 'moment';
import { setCurrentTime } from '../../../../redux/features/doctor/Dashboard/consultation/consultation';

type Props = {
    children?: React.ReactNode
};

const Settings: React.FC<Props> = ({children}) => {

    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch(); 
    const location = useLocation();

    const colors = useAppSelector(state => state.theme.colors);
    const accountInfo = useAppSelector(state => state.meta.accountInfo); 
    const groupAccess = useAppSelector(state => state.group.groupAccess); 

    const isNavigationDropdownOpened = useAppSelector(state => state.navigation.isNavigationDropdownOpened);

    const [timeLeft, setTimeLeft] = useState(2); 
    const [isHovering, setIsHovering] = useState(false);

    const [navbarTime, setNavbarTime] = useState(''); 
    const [settingsTimeLeft, setSettingsTimeLeft] = useState(0); 

    const isMobile = useMediaQuery('(orientation: portrait)');

    // -- 

    document.addEventListener('mousedown', function(e) {

        if (isNavigationDropdownOpened) { 

            if (!isHovering) { 

                setSettingsTimeLeft(2); 
    
            };

        }; 

    });

    // -- 

    const handleDropdown = () => { 

        dispatch(setIsNavigationDropdownOpened(!isNavigationDropdownOpened)); 
        setSettingsTimeLeft(0);

    }; 

    // -- 

    const handleLockScreen = () => { 

        navigate('/lock/screen'); 

    }; 

    // -- 

    useEffect(() => {

        dispatch(setIsNavigationDropdownOpened(false)); 

    },[location.pathname]); 

    // -- 


    useEffect(() => {
        let myInterval = setInterval(() => {
          if (timeLeft > 0) {
            setTimeLeft(timeLeft - 1);
          }
      
          if (timeLeft === 1) {
            // Set locale based on i18n language
            moment.locale(i18n.language);
      
            const formatted = moment().utc(true).format('LLLL');
            setNavbarTime(formatted.charAt(0).toUpperCase() + formatted.slice(1));
            setTimeLeft(2);
          }
      
          if (timeLeft === 0) {
            clearInterval(myInterval);
          }
        }, 500);
      
        return () => {
          clearInterval(myInterval);
        };
      }, [timeLeft, i18n.language]);

      // -- 

      useEffect(() => {
        let myInterval = setInterval(() => {
          if (settingsTimeLeft > 0) {
            setSettingsTimeLeft(timeLeft - 1);
          }
      
          if (settingsTimeLeft === 1) {

            dispatch(setIsNavigationDropdownOpened(false)); 

          }
      
          if (settingsTimeLeft === 0) {
            clearInterval(myInterval);
          }
        }, 100);
      
        return () => {
          clearInterval(myInterval);
        };
      }, [settingsTimeLeft]);

    
    // -- 

    return (

        <Box
            sx={{ 
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space',
                position: 'absolute', 
                right: '2vw', 
            }}
        >

            {!isMobile &&
                <Box
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: 1,
                        fontSize: 12, 
                        color: 'rgb(150, 150, 150)'
                    }}
                    >{`${accountInfo?.firstName} ${accountInfo?.lastName}`}
                </Box>
            }
            {!isMobile &&
                <Box
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: 1,
                        fontSize: 12, 
                        color: 'rgb(150, 150, 150)'
                    }}
                    >{`|`}
                </Box>
            }
            {!isMobile &&
                <Box
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginRight: 2,
                        fontSize: 12, 
                        color: 'rgb(150, 150, 150)'
                    }}
                >{navbarTime}
                </Box>
            }
            <LockPersonIcon 
                onClick={handleLockScreen}
                sx={{ 
                    color: 'rgb(100, 100, 100)',
                    marginRight: 2,
                    cursor: 'pointer',
                    '&:hover': { color: colors?.mainHover }
                }} 
            /> 

            <AccountCircleIcon 
                onClick={handleDropdown}
                sx={{ 
                    cursor: 'pointer', 
                    color: isNavigationDropdownOpened ? colors?.main : 'rgb(100, 100, 100)',
                    '&:hover': { color: colors?.mainHover }
                }} />
            
                <Box
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'space-evenly',
                        width: 200,
                        height: isNavigationDropdownOpened ? 200 : 0,
                        top: 30, 
                        right: 0, 
                        position: 'absolute',
                        backgroundColor: colors?.fileExplorerDropdown,
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                        borderRadius: 4,
                        zIndex: 10000000, 
                        transition: 'height 0.3s',
                    }}
                >

                    {isNavigationDropdownOpened && 
                    
                    <>

                        <SettingsRow section={'dashboard'} >
                            <DashboardOutlinedIcon /> 
                        </SettingsRow>

                        {groupAccess?.fullAccess && 
                        <SettingsRow section={'group'} customTitle={'groupPortal'}>
                            <BubbleChartOutlinedIcon /> 
                        </SettingsRow>}

                        <SettingsRow section={'support'} >
                            <SupportAgentOutlinedIcon /> 
                        </SettingsRow>

                        <SettingsRow section={'legal'} >
                            <PolicyOutlinedIcon /> 
                        </SettingsRow>

                        <SettingsRow section={'settings'} >
                            <SettingsOutlinedIcon /> 
                        </SettingsRow>

                        <SettingsRow section={'logout'} >
                            <LogoutOutlinedIcon /> 
                        </SettingsRow>

                    </>}

                </Box>

        </Box>

    );
};

export default Settings;